import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FolderPlus,
  Filter,
  X,
  Grid3x3,
  List,
} from "lucide-react";
import { getProjects, deleteProject } from "../api/projects";
import { getTasks } from "../api/tasks";
import { getProjectRoles } from "../api/roles";
import { getAllUsers } from "../api/users";
import { fetchMilestones } from "../api/milestones";
import { getProgrammes } from "../api/programmes";
import { Project } from "../types/project";
import { Task } from "../types/task";
import { ProjectRole } from "../types/role";
import { User } from "../types/user";
import { Milestone } from "../types/milestone";
import { Programme } from "../api/programmes";
import ProjectForm from "../components/ProjectForm";
import DeleteProjectModal from "../components/DeleteProjectModal";
import ProjectTableView from "../components/ProjectTableView";
import ProjectCard from "../components/ProjectCard";
import ManageTeamModal from "../components/ManageTeamModal";
import Button from "../components/Button";
import Select from "../components/Select";
import Breadcrumb from "../components/Breadcrumb";
import { CardSkeleton } from "../components/LoadingSkeleton";
import { showToast } from "../utils/toast";
import { usePermissions } from "../hooks/usePermissions";
import { useAuth } from "../contexts/AuthContext";

const ProjectsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { canEditProject } = usePermissions();
  const { user, isAdmin, isTenantViewer, hasNoActiveTenant } = useAuth();
  // Read-only members of the workspace cannot create, and neither can a session with no
  // workspace to create in — the API refuses both.
  const canCreateProjects = !!user && !isTenantViewer && !hasNoActiveTenant;
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projectRoles, setProjectRoles] = useState<ProjectRole[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingProject, setEditingProject] = useState<Project | undefined>(
    undefined
  );
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);
  const [managingRolesFor, setManagingRolesFor] = useState<string | null>(null);
  const [showRoleForm, setShowRoleForm] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<ProjectRole | undefined>(
    undefined
  );
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [programmeFilter, setProgrammeFilter] = useState<string>("");
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(() => {
    const saved = localStorage.getItem('projectsView');
    return (saved === 'table' || saved === 'cards') ? saved : 'cards';
  });

  const loadProjects = useCallback(async (programmeId?: string) => {
    try {
      setLoading(true);
      const [
        projectsData,
        tasksData,
        rolesData,
        usersData,
        milestonesData,
        programmesData,
      ] = await Promise.all([
        getProjects(programmeId),
        getTasks(),
        getProjectRoles(),
        getAllUsers(),
        fetchMilestones(),
        getProgrammes(),
      ]);
      setProjects(projectsData);
      setTasks(tasksData);
      setProjectRoles(rolesData);
      setUsers(usersData);
      setMilestones(milestonesData);
      setProgrammes(programmesData);
      setError(null);
    } catch (err) {
      setError("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, []);

  // Persist view mode to localStorage
  useEffect(() => {
    localStorage.setItem('projectsView', viewMode);
  }, [viewMode]);

  // Handle navigation state from dashboard
  useEffect(() => {
    const state = location.state as {
      filterType?: string;
      programmeId?: string;
      autoApply?: boolean;
    } | null;
    
    if (state?.autoApply) {
      if (state.filterType) {
        setActiveFilter(state.filterType);
      }
      if (state.programmeId) {
        setProgrammeFilter(state.programmeId);
        loadProjects(state.programmeId);
      }
    }
  }, [location.state, loadProjects]);

  const handleSuccess = (updatedProject: Project) => {
    if (editingProject) {
      setProjects(
        projects.map((p) => (p.id === updatedProject.id ? updatedProject : p))
      );
      setEditingProject(undefined);
      showToast.success("Project updated successfully!");
    } else {
      setProjects([...projects, updatedProject]);
      showToast.success("Project created successfully!");
    }
    setShowForm(false);
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setShowForm(true);
  };

  const handleDeleteClick = (project: Project) => {
    setDeletingProject(project);
  };

  const handleDeleteConfirm = async (cascade: boolean, reassignTo?: string) => {
    if (!deletingProject) return;

    try {
      await deleteProject(deletingProject.id, cascade, reassignTo);
      setProjects(projects.filter((p) => p.id !== deletingProject.id));

      // Reload tasks to reflect changes
      const updatedTasks = await getTasks();
      setTasks(updatedTasks);

      setDeletingProject(null);
      setError(null);
      showToast.success("Project deleted successfully!");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete project";
      setError(errorMessage);
      showToast.error(errorMessage);
      setDeletingProject(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeletingProject(null);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingProject(undefined);
  };

  const handleManageRoles = (projectId: string) => {
    setManagingRolesFor(projectId);
    setShowRoleForm(false);
    setEditingRole(undefined);
  };

  const handleCloseRoles = () => {
    setManagingRolesFor(null);
    setShowRoleForm(false);
    setEditingRole(undefined);
  };

  const handleRoleSuccess = async () => {
    // Reload project roles
    const rolesData = await getProjectRoles();
    setProjectRoles(rolesData);
    setShowRoleForm(false);
    setEditingRole(undefined);
  };

  const handleEditRole = (role: ProjectRole) => {
    setEditingRole(role);
    setShowRoleForm(true);
  };

  const getUserName = (userId: string): string => {
    if (!users || !Array.isArray(users)) return "Unknown User";
    const user = users.find((u) => u.id === userId);
    return user ? user.full_name || user.username : "Unknown User";
  };

  const getManagerName = (managerId?: string): string => {
    if (!managerId) return "No Manager";
    if (!users || !Array.isArray(users)) return "Unknown User";
    const user = users.find((u) => u.id === managerId);
    return user ? user.full_name || user.username : "Unknown User";
  };

  const getProgrammeName = (programmeId?: string): string => {
    if (!programmeId) return "No Programme";
    if (!programmes || !Array.isArray(programmes)) return "Unknown Programme";
    const programme = programmes.find((p) => p.id === programmeId);
    return programme ? programme.name : "Unknown Programme";
  };

  const handleProgrammeFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = e.target.value;
    setProgrammeFilter(value);
    loadProjects(value || undefined);
  };

  const handleView = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const handleAddTask = (projectId: string) => {
    // Navigate to tasks page with project pre-selected
    window.location.href = `/tasks?project=${projectId}&add=true`;
  };

  const handleAddMilestone = (projectId: string) => {
    // Navigate to milestones page with project pre-selected
    window.location.href = `/milestones?project=${projectId}&add=true`;
  };

  const handleViewTasks = (projectId: string) => {
    // Navigate to tasks page with project filter applied
    navigate('/tasks', { 
      state: { 
        filterType: 'project', 
        projectId, 
        autoApply: true 
      } 
    });
  };

  const handleViewMilestones = (projectId: string) => {
    // Navigate to milestones page with project filter applied
    navigate('/milestones', { 
      state: { 
        projectId, 
        autoApply: true 
      } 
    });
  };

  if (loading) {
    return (
      <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="h-10 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          <div className="h-10 w-40 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
        </div>
      </div>
    );
  }

  // Helper function to calculate metrics for a specific project
  const getProjectMetrics = (projectId: string) => {
    const projectTasks = tasks.filter((t) => t.project_id === projectId);
    const totalTasks = projectTasks.length;
    const completedTasks = projectTasks.filter(
      (t) => t.status === "completed"
    ).length;
    const overdueTasks = projectTasks.filter((t) => {
      if (!t.due_date || t.status === "completed") return false;
      return new Date(t.due_date) < new Date();
    }).length;
    const completionRate =
      totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    const milestoneCount = milestones.filter(
      (m) => m.project_id === projectId
    ).length;

    return {
      totalTasks,
      completedTasks,
      overdueTasks,
      completionRate,
      milestoneCount,
    };
  };

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
      {/* Breadcrumb - only show when programme filter active */}
      {programmeFilter && (
        <Breadcrumb
          items={[
            { label: 'Programmes', path: '/programmes' },
            { label: getProgrammeName(programmeFilter), path: '/programmes' },
            { label: 'Projects' }
          ]}
        />
      )}
      
      <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">Projects</h1>
        <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
          {/* View Toggle */}
          <div className="flex gap-2 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/50 rounded-lg p-1">
            <Button
              variant={viewMode === 'cards' ? 'primary' : 'outline'}
              size="sm"
              leftIcon={Grid3x3}
              onClick={() => setViewMode('cards')}
              title="Card View"
            >
              Cards
            </Button>
            <Button
              variant={viewMode === 'table' ? 'primary' : 'outline'}
              size="sm"
              leftIcon={List}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              Table
            </Button>
          </div>
          {canCreateProjects && (
            <Button
              onClick={() => setShowForm(!showForm)}
              variant="primary"
              leftIcon={showForm ? X : FolderPlus}
            >
              {showForm ? "Hide Form" : "Add New Project"}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30 text-error">
          {error}
        </div>
      )}

      {/* Programme Filter */}
      <div className="mb-4 max-w-xs">
        <Select
          label="Filter by Programme"
          value={programmeFilter}
          onChange={handleProgrammeFilterChange}
        >
          <option value="">All Programmes</option>
          {programmes.map((programme) => (
            <option key={programme.id} value={programme.id}>
              {programme.name}
            </option>
          ))}
        </Select>
      </div>

      {/* Active Filter Indicator */}
      {(activeFilter || programmeFilter) && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border-2 border-primary-500 dark:border-primary-700 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              <div className="flex flex-col">
                {activeFilter && (
                  <span className="font-semibold text-primary-900 dark:text-primary-100">
                    Active Filter:{" "}
                    {activeFilter === "empty"
                      ? "Empty Projects (No Tasks)"
                      : activeFilter}
                  </span>
                )}
                {programmeFilter && (
                  <span className="font-semibold text-primary-900 dark:text-primary-100">
                    Programme: {getProgrammeName(programmeFilter)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {activeFilter && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveFilter(null)}
                >
                  Clear Filter
                </Button>
              )}
              {programmeFilter && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setProgrammeFilter('');
                    loadProjects();
                  }}
                >
                  Clear Programme
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="mb-8">
          <ProjectForm
            project={editingProject}
            onSuccess={handleSuccess}
            onCancel={handleCancelForm}
          />
        </div>
      )}

      {deletingProject && (
        <DeleteProjectModal
          project={deletingProject}
          taskCount={
            tasks.filter((t) => t.project_id === deletingProject.id).length
          }
          availableProjects={projects}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}

      {/* Manage Team Modal */}
      {managingRolesFor && (
        <ManageTeamModal
          project={projects.find(p => p.id === managingRolesFor)!}
          projectRoles={projectRoles}
          users={users}
          onClose={handleCloseRoles}
          onRoleSuccess={handleRoleSuccess}
        />
      )}

      {projects.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          No projects found. Create your first project above!
        </div>
      ) : viewMode === 'table' ? (
        <ProjectTableView
          projects={projects.filter((project) => {
            // Apply empty project filter if active
            if (activeFilter === "empty") {
              const projectTasks = tasks.filter(
                (t) => t.project_id === project.id
              );
              return projectTasks.length === 0;
            }
            return true;
          })}
          tasks={tasks}
          users={users}
          programmes={programmes}
          milestones={milestones}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onManageRoles={handleManageRoles}
          onAddTask={handleAddTask}
          onAddMilestone={handleAddMilestone}
          onViewTasks={handleViewTasks}
          onViewMilestones={handleViewMilestones}
          canEdit={canEditProject}
          canManageRoles={isAdmin}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects
            .filter((project) => {
              // Apply empty project filter if active
              if (activeFilter === "empty") {
                const projectTasks = tasks.filter(
                  (t) => t.project_id === project.id
                );
                return projectTasks.length === 0;
              }
              return true;
            })
            .map((project) => {
              const metrics = getProjectMetrics(project.id);

              return (
                <ProjectCard
                  key={project.id}
                  project={project}
                  metrics={metrics}
                  projectRoles={projectRoles}
                  users={users}
                  managingRolesFor={managingRolesFor}
                  showRoleForm={showRoleForm}
                  editingRole={editingRole}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  onManageRoles={handleManageRoles}
                  onAddTask={handleAddTask}
                  onAddMilestone={handleAddMilestone}
                  onViewTasks={handleViewTasks}
                  onViewMilestones={handleViewMilestones}
                  onRoleFormToggle={() => setShowRoleForm(!showRoleForm)}
                  onRoleSuccess={handleRoleSuccess}
                  onRoleCancel={() => {
                    setShowRoleForm(false);
                    setEditingRole(undefined);
                  }}
                  onEditRole={handleEditRole}
                  onCloseRoles={handleCloseRoles}
                  getManagerName={getManagerName}
                  getProgrammeName={getProgrammeName}
                  getUserName={getUserName}
                  canEdit={canEditProject(project.id)}
                  canManageRoles={isAdmin}
                />
              );
            })}
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
